import React, { useCallback, useEffect, useState } from "react";
import MUIBox from "@mui/material/Box";
import MUIButton from "@mui/material/Button";
import MUIButtonGroup from "@mui/material/ButtonGroup";
import Widget from "../../Widget";
import Setting from "../../../Utility/Setting";
import Format from "../../../Utility/Format";

/**
 * Builds the dashboard configuration object for the accounts widget.
 *
 * @param {string} accountStatusFilter The account status filter ("active", "top", or "target").
 * @param {object} widgetData The widget data returned from the dashboard widget endpoint.
 * @returns {object} The dashboard configuration object.
 */

function buildDashboardConfiguration(accountStatusFilter, widgetData) {
    const dashboardConfiguration = {
        dateRange: {
            beginDate: widgetData.meta.beginDate,
            endDate: widgetData.meta.endDate,
        },
        regionLocation: {
            regionIds: Setting.get('queue.prefilters.regionIds') || [],
            locationIds: Setting.get('queue.prefilters.locationIds') || [],
        },
        user: {
            userprofileIds: Setting.get('queue.prefilters.userIds') || [],
        },
        onlyActiveAccounts: {
            activeAccounts: true,
        },
    };

    if (accountStatusFilter !== "active") {
        dashboardConfiguration.accountStatus = {
            status: accountStatusFilter,
        };
    }

    return dashboardConfiguration;
}


/**
 * Sales queue dashboard widget: accounts
 *
 * @param {object} props
 * @param {object} props.queue Current queue record from context.
 * @param {array} props.prefilters Prefilter objects from the queue screen (same shape as `getQueueRows`).
 * @param {function} props.onReloadQueueRows Reloads queue rows and statistics after refresh.
 * @returns {React.ReactElement}
 */

export default function WidgetDashboardAccounts(props) {
    const [accountStatusFilter, setAccountStatusFilter] = useState(
        Setting.get(`dashboard.${props.widgetId}.accountStatusFilter`) || "active"
    );

    const [dashboardWidgetEndpointPayload, setDashboardWidgetEndpointPayload] = useState(null);
    const [isDashboardWidgetDataLoading, setIsDashboardWidgetDataLoading] = useState(true);
    const [isDashboardWidgetDataError, setIsDashboardWidgetDataError] = useState(false);

    const handleWidgetRenderPropertiesChange = useCallback((widgetRenderProperties) => {
        setDashboardWidgetEndpointPayload(widgetRenderProperties.widgetData);
        setIsDashboardWidgetDataLoading(widgetRenderProperties.isLoadingWidgetData);
        setIsDashboardWidgetDataError(widgetRenderProperties.isErrorWidgetData);
    }, []);

    useEffect(() => {
        const hasSuccessfulPayload =
            isDashboardWidgetDataLoading === false &&
            isDashboardWidgetDataError === false &&
            dashboardWidgetEndpointPayload !== null &&
            dashboardWidgetEndpointPayload !== undefined;

        if (props.isActive && hasSuccessfulPayload && accountStatusFilter !== null) {
            props.updateActiveWidgetConfiguration({
                dashboardConfiguration: buildDashboardConfiguration(
                    accountStatusFilter,
                    dashboardWidgetEndpointPayload
                )
            });
        }
    }, [
        props.isActive,
        isDashboardWidgetDataLoading,
        isDashboardWidgetDataError,
        dashboardWidgetEndpointPayload,
        accountStatusFilter,
        props.updateActiveWidgetConfiguration,
    ]);

    /**
     * Handles button click for account filter selection.
     *
     * @param {string} nextAccountStatusFilter The account status filter ("active", "top", or "target").
     * @param {object} widgetData The widget data returned from the dashboard widget endpoint.
     */

    const handleAccountFilterButtonClick = (nextAccountStatusFilter, widgetData) => {
        setAccountStatusFilter(nextAccountStatusFilter);
        Setting.set(`dashboard.${props.widgetId}.accountStatusFilter`, nextAccountStatusFilter);
        props.updateActiveWidgetConfiguration({
            dashboardConfiguration: buildDashboardConfiguration(nextAccountStatusFilter, widgetData),
        });
        props.onActiveWidgetChange();
    };

    return (
        <Widget
            getWidgetId={() => props.widgetId}
            getTitle={() => props.dashboardWidget.name}
            isActive={() => props.isActive}
            onWidgetRenderPropertiesChange={handleWidgetRenderPropertiesChange}
            renderContent={({ widgetData }) => (
                <MUIBox sx={{ height: "80%", display: "flex", justifyContent: "flex-end", mt: 1 }}>
                    <MUIButtonGroup
                        size="small"
                        fullWidth
                    >
                        <MUIButton
                            variant={props.isActive && accountStatusFilter === "active" ? "contained" : "outlined"}
                            onClick={() => handleAccountFilterButtonClick("active", widgetData)}
                            sx={{ lineHeight: 1.2 }}
                        >
                            Active <br /> {Format.number(widgetData.activeAccounts)}
                        </MUIButton>
                        <MUIButton
                            variant={props.isActive && accountStatusFilter === "top" ? "contained" : "outlined"}
                            onClick={() => handleAccountFilterButtonClick("top", widgetData)}
                            sx={{ lineHeight: 1.2 }}
                        >
                            Top <br /> {Format.number(widgetData.topAccounts)}
                        </MUIButton>
                        <MUIButton
                            variant={props.isActive && accountStatusFilter === "target" ? "contained" : "outlined"}
                            onClick={() => handleAccountFilterButtonClick("target", widgetData)}
                            sx={{ lineHeight: 1.2 }}
                        >
                            Target <br /> {Format.number(widgetData.targetAccounts)}
                        </MUIButton>
                    </MUIButtonGroup>
                </MUIBox>
            )}
        />
    );
}

